





























import SearchFormLayout from './SearchFormLayout.vue';
import SearchInput from './SearchInput.vue';
import ChooseGroup from './ChooseGroup.vue';
import Place from '@/models/place.model';
import SearchTermChangeEvent from '@/models/search-term-change-event.model';
import Config from '@/models/config.model';
import Translations from '@/models/translations.model';
import Button from './Button.vue';
import State from '@/classes/state';
import FilterGroupFilterCombi from '@/classes/filter-group-filter-combi';
import { Map } from 'leaflet';
import geocode from '@/utility/geocoder';
import getCurrentPosition from '@/utility/geolocation';

export default {
    name: 'SearchForm',
    components: {
        SearchFormLayout,
        SearchInput,
        ChooseGroup,
        Button,
    },
    data() {
        const state: State = this.$store.state;

        return {
            config: state.config,
            translations: state.translations,
            filters: state.filters,
            errorMessage: null,
            formSubmitted: false,
            formValid: true,
            formIsLoading: false,
            geolocationIsLoading: false,
            initialSearchTerm: state.searchTerm,
        };
    },
    computed: {
        selectedFilters(): Array<FilterGroupFilterCombi> {
            return (this.$store.state as State).selectedFilters;
        },
        map(): Map | null {
            return (this.$store.state as State).map;
        },
        searchTerm(): string {
            return (this.$store.state as State).searchTerm;
        },
        mapInitialized(): boolean {
            return (this.$store.state as State).mapInitialized;
        },
        place(): Place | null {
            return (this.$store.state as State).myPlace;
        },
    },
    watch: {
        // selectedFilters(): void {
        //     if (!this.formValid || !this.mapInitialized) {
        //         // form is invalid or map is not initialized
        //         // so dont load dealers until
        //         // the user submits/corrects the form
        //         return;
        //     }

        //     this.$store.dispatch('loadDealersByMapBounds');
        // },
        async mapInitialized(newValue: boolean, oldValue: boolean): Promise<void> {
            const state: State = this.$store.state;

            if (!oldValue && newValue && state.myPlace) {
                // Check if map is initialized
                // and if there is a selected place.
                // If so, the map was lazy initialized
                // so we now scroll to the map since we know
                // that the map is available in the DOM
                await this.$store.dispatch('loadMinDealersByPlace');
                this.formIsLoading = false;

                setTimeout(() => {
                    this.scrollMapIntoView(); 
                }, 250);
            }
        },
    },
    methods: {
        /**
         * Executed when the form is submitted
         */
        handleFormSubmit(e: Event): void {
            e.preventDefault();
            this.submitForm();
        },
        async submitForm(): Promise<void> {
            this.validateSearchTerm();

            if (!this.formValid) {
                return;
            }

            this.formIsLoading = true;

            if (!this.formSubmitted) {
                // if form wasnt submitted with the
                // current searchterm goecode
                // the searchterm
                const place = await this.geocodeSearchTerm();

                if (!place) {
                    this.formIsLoading = false;
                    return;
                }

                this.$store.dispatch('setMyPlace', place);
            }

            this.formSubmitted = true;

            if (this.mapInitialized) {
                // if the map is already shown
                // scroll to it,
                // otherwise the watcher
                // function will scroll to
                // it as soon as the map loaded
                await this.$store.dispatch('loadMinDealersByPlace');
                this.formIsLoading = false;

                this.scrollMapIntoView(); 
            } else {
                this.$store.dispatch('setShowMap', true);
            }

            this.$store.dispatch('replaceState');
        },
        async geocodeSearchTerm(): Promise<Place | null> {
            const config: Config = this.config;
            let geocodeResults: Array<google.maps.GeocoderResult> | null;

            try {
                geocodeResults = await geocode({
                    address: this.searchTerm,
                    componentRestrictions: {
                        country: config.map.providers.google.geocode.country,
                    },
                });
            } catch (error) {
                if (error === 'ZERO_RESULTS') {
                    geocodeResults = [];
                } else {
                    // console.log('Geocode was not successful for the following reason: ' + error);
                    return null;
                }
            }

            if (!geocodeResults || !geocodeResults.length) {
                const translations: Translations = this.translations;
                this.formValid = false;
                this.scrollFormIntoView();
                this.errorMessage = translations.searchTermNoResults;
                return null;
            }

            const firstResult = geocodeResults[0];
            const place: Place = {
                lat: firstResult.geometry.location.lat(),
                lng: firstResult.geometry.location.lng()
            };

            return place;
        },
        validateSearchTerm(): void {
            const config: Config = this.config;
            const translations: Translations = this.translations;
            const { searchTermMinLength } = config;

            const trimmedSearchTerm = this.searchTerm?.trim();

            if (!trimmedSearchTerm) {
                this.formValid = false;
                this.scrollFormIntoView();
                this.errorMessage = translations.searchTermInvalid;
            } else if (trimmedSearchTerm.length < searchTermMinLength) {
                this.formValid = false;
                this.scrollFormIntoView();
                this.errorMessage = translations.searchTermTooShort;
            } else {
                this.formValid = true;
            }
        },
        /**
         * Executed when the input changes
         */
        handleInputChange(e: SearchTermChangeEvent): void {
            this.formSubmitted = false;
            this.formValid = true;

            this.$store.dispatch('setSearchTerm', e.newVal);
        },
        /**
         * Executed when user selects a place
         * using google maps autocomplete
         */   
        handlePlaceChange(place: Place): void {
            /**
             * handlePlaceChange is called before
             * handleInputChange so we use a timeout
             * to ensure that handlePlaceChange is called after
             * handleInputChange
             */
            setTimeout(async () => {
                this.formSubmitted = true;
                this.formIsLoading = true;

                this.$store.dispatch('setMyPlace', place);
                
                if (this.mapInitialized) {
                    // if the map is already shown
                    // load dealers and scroll to it,
                    // otherwise the watcher
                    // function will scroll to
                    // it as soon as the map loaded
                    await this.$store.dispatch('loadMinDealersByPlace');
                    this.formIsLoading = false;

                    this.scrollMapIntoView();
                } else {
                    this.$store.dispatch('setShowMap', true);
                }

                this.$store.dispatch('replaceState');
            }, 0);
        },
        scrollFormIntoView() {
            this.scrollIntoView(this.$el);
        },
        scrollMapIntoView() {
            const map = document.querySelector('#dealer-search-map');
            this.scrollIntoView(map);
        },
        scrollIntoView(element: HTMLElement, timeout = 200) {
            setTimeout(() => {
                element?.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth'
                });
            }, timeout);
        },
        /**
         * Executed when the user clicks
         * the location button
         */
        async handleLocationButtonClick(): Promise<void> {
            this.geolocationIsLoading = true;

            const place: Place | null = await this.getGeolocation();

            this.geolocationIsLoading = false;

            if (!place) {
                return;
            }

            const geocoderResult: google.maps.GeocoderResult | null = await this.geocodeGeolocation(place);

            if (!geocoderResult) {
                return;
            }

            this.initialSearchTerm = '';
    
            // the timeout is used so that vue detects
            // the changed initialSearchTerm.
            // Without the timeout there are cases
            // when the initialSearchTerm from the URL
            // is the same as the geocoded search term
            // so that the input field won't update
            setTimeout(() => {
                const formattedAddress = geocoderResult.formatted_address;
                this.initialSearchTerm = formattedAddress;

                this.$store.dispatch('setSearchTerm', formattedAddress);
                this.$store.dispatch('replaceState');

                this.handlePlaceChange(place);
            });
        },
        /**
         * Returns the place
         * for the users geolocation
         * or null if an error occures
         * or the user denied the GPS access
         */
        async getGeolocation(): Promise<Place | null> {
            let place: Place | null;

            try {
                place = await getCurrentPosition();
            } catch {
                place = null;
            }

            return place;
        },
        /**
         * Geocodes the given
         * place object
         */
        async geocodeGeolocation(geolocation: Place): Promise<google.maps.GeocoderResult | null> {
            let geocodeResults: Array<google.maps.GeocoderResult> | null;

            try {
                geocodeResults = await geocode({
                    location: geolocation,
                });
            } catch (error) {
                if (error === 'ZERO_RESULTS') {
                    geocodeResults = [];
                } else {
                    // console.log('Geocode was not successful for the following reason: ' + error);
                    return null;
                }
            }

            if (!geocodeResults || !geocodeResults.length) {
                return null;
            }

            return geocodeResults[0];
        },
        autoSubmitForm() {
            const state: State = this.$store.state;

            if (state.submit) {
                this.submitForm();
            }
        },
    },
    mounted() {
        this.autoSubmitForm();
    },
};
